import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Filter, DollarSign } from 'lucide-react';
import PageHeader from '@/components/common/PageHeader';
import SearchInput from '@/components/common/SearchInput';
import DataTable, { type Column } from '@/components/common/DataTable';
import StatusBadge from '@/components/common/StatusBadge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorBanner from '@/components/common/ErrorBanner';
import { useServices, useCreateService } from '@/hooks/useServices';
import { useCostByService, useCostByServiceEnv } from '@/hooks/useCosts';
import type { Service, CreateServiceRequest, ServiceGroup } from '@/types';

const ENV_OPTIONS = ['', 'dev', 'sandbox', 'staging', 'prod', 'qa', 'test', 'unknown'] as const;

const GROUPS: { value: ServiceGroup | ''; label: string; pillClass: string }[] = [
  { value: '', label: 'All', pillClass: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  { value: 'backend', label: 'Backend', pillClass: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  { value: 'frontend', label: 'Frontend', pillClass: 'bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400' },
  { value: 'data_eng', label: 'Data Eng', pillClass: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  { value: 'advisor', label: 'Advisor', pillClass: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  { value: 'analytics', label: 'Analytics', pillClass: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  { value: 'dcc', label: 'DCC', pillClass: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  { value: 'platform', label: 'Platform', pillClass: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  { value: 'other', label: 'Other', pillClass: 'bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
];

function groupPillClass(group?: string | null): string {
  const found = GROUPS.find((g) => g.value === group);
  return found?.pillClass ?? 'bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
}

function formatUsd(n?: number | null): string {
  if (n == null) return '—';
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

export default function ServiceCatalog() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [group, setGroup] = useState<ServiceGroup | ''>('');
  const [env, setEnv] = useState<string>('');
  const [lifecycle, setLifecycle] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const pageSize = 20;
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data, isLoading, error, refetch } = useServices({
    search: search || undefined,
    service_type: serviceType || undefined,
    group: group || undefined,
    env: env || undefined,
    lifecycle_stage: lifecycle || undefined,
    sort: sortKey || undefined,
    order: sortKey ? sortDir : undefined,
    page,
    page_size: pageSize,
  });

  const { data: costsByService } = useCostByService(30);
  const { data: costsByEnv } = useCostByServiceEnv(30);

  const costBySlug = useMemo(() => {
    const out = new Map<string, number>();
    if (env) {
      for (const row of costsByEnv ?? []) {
        if (row.service_slug && row.environment === env) {
          out.set(row.service_slug, (out.get(row.service_slug) ?? 0) + (row.total_cost ?? 0));
        }
      }
    } else {
      for (const row of costsByService ?? []) {
        if (row.service_slug) {
          out.set(row.service_slug, (out.get(row.service_slug) ?? 0) + (row.total_cost ?? 0));
        }
      }
    }
    return out;
  }, [costsByService, costsByEnv, env]);

  const createMutation = useCreateService();

  const handleRowClick = useCallback(
    (service: Service) => navigate(`/services/${service.slug}`),
    [navigate],
  );

  const columns: Column<Service>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium text-slate-900 dark:text-slate-100">{row.name}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500">{row.slug}</p>
        </div>
      ),
    },
    {
      key: 'service_group',
      header: 'Group',
      sortable: true,
      render: (row) => (
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${groupPillClass(row.service_group)}`}
        >
          {row.service_group ? row.service_group.replace('_', ' ') : 'other'}
        </span>
      ),
    },
    {
      key: 'cloud_accounts',
      header: 'Clouds / Envs',
      render: (row) => {
        const accounts = row.cloud_accounts ?? [];
        if (accounts.length === 0)
          return <span className="text-slate-400 dark:text-slate-500 text-xs">unmapped</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {accounts.slice(0, 4).map((ca) => (
              <span
                key={ca.id}
                className="inline-flex items-center rounded-md bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:text-slate-300"
                title={`${ca.provider} · ${ca.account_identifier} · ${ca.environment}`}
              >
                {ca.provider}·{ca.environment}
              </span>
            ))}
            {accounts.length > 4 && (
              <span className="text-[10px] text-slate-400 dark:text-slate-500">+{accounts.length - 4}</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'cost_30d',
      header: env ? `30d Cost (${env})` : '30d Cost',
      sortable: true,
      sortValue: (row) => costBySlug.get(row.slug) ?? null,
      render: (row) => {
        const cost = costBySlug.get(row.slug);
        return (
          <span className="text-slate-700 dark:text-slate-200 font-medium tabular-nums">
            {formatUsd(cost ?? null)}
          </span>
        );
      },
    },
    {
      key: 'service_type',
      header: 'Type',
      sortable: true,
      render: (row) => (
        <span className="inline-flex items-center rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-300 capitalize">
          {row.service_type}
        </span>
      ),
    },
    {
      key: 'team_name',
      header: 'Team',
      sortable: true,
      render: (row) => <span className="text-slate-600 dark:text-slate-400">{row.team_name ?? '-'}</span>,
    },
    {
      key: 'lifecycle_stage',
      header: 'Lifecycle',
      sortable: true,
      render: (row) => <StatusBadge status={row.lifecycle_stage} />,
    },
  ];

  const handleCreateSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const payload: CreateServiceRequest = {
      name: formData.get('name') as string,
      slug: formData.get('slug') as string,
      service_type: (formData.get('service_type') as CreateServiceRequest['service_type']) || 'api',
      service_group:
        ((formData.get('service_group') as string) || undefined) as ServiceGroup | undefined,
      description: (formData.get('description') as string) || undefined,
      language: (formData.get('language') as string) || undefined,
    };
    try {
      await createMutation.mutateAsync(payload);
      setShowCreateModal(false);
    } catch {
      // Error is displayed by mutation state
    }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Service Catalog"
        description="Browse and manage all registered services across your platform."
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Register Service
          </button>
        }
      />

      {/* Group filter chip bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl bg-surface-50 dark:bg-slate-800/50 p-2">
        {GROUPS.map((g) => {
          const active = group === g.value;
          return (
            <button
              key={g.value || 'all'}
              onClick={() => {
                setGroup(g.value);
                setPage(1);
              }}
              className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                active
                  ? 'bg-brand-600 text-white shadow-sm ring-2 ring-brand-500/30'
                  : `${g.pillClass} hover:opacity-80`
              }`}
            >
              {g.label}
            </button>
          );
        })}
      </div>

      {/* Other filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
          placeholder="Search services..."
          className="w-72"
        />
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400 dark:text-slate-500" />
          <select
            value={env}
            onChange={(e) => { setEnv(e.target.value); setPage(1); }}
            className="select-field"
            aria-label="Environment filter"
          >
            <option value="">All Envs</option>
            {ENV_OPTIONS.filter((e) => e).map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
          <select
            value={serviceType}
            onChange={(e) => { setServiceType(e.target.value); setPage(1); }}
            className="select-field"
          >
            <option value="">All Types</option>
            <option value="api">API</option>
            <option value="frontend">Frontend</option>
            <option value="worker">Worker</option>
            <option value="library">Library</option>
            <option value="infrastructure">Infrastructure</option>
          </select>
          <select
            value={lifecycle}
            onChange={(e) => { setLifecycle(e.target.value); setPage(1); }}
            className="select-field"
          >
            <option value="">All Stages</option>
            <option value="active">Active</option>
            <option value="deprecated">Deprecated</option>
            <option value="experimental">Experimental</option>
          </select>
        </div>
        <div className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
          <DollarSign className="h-3.5 w-3.5" />
          {env ? `30-day cost in ${env}` : '30-day cost across all envs'}
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="py-16"><LoadingSpinner message="Loading services..." /></div>
        ) : error ? (
          <div className="p-4"><ErrorBanner message="Failed to load services" onRetry={refetch} /></div>
        ) : (
          <DataTable
            columns={columns}
            data={data?.items ?? []}
            keyExtractor={(row) => row.id}
            onRowClick={handleRowClick}
            page={page}
            pageSize={pageSize}
            total={data?.total}
            onPageChange={setPage}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={(key, dir) => {
              setSortKey(key);
              setSortDir(dir);
              setPage(1);
            }}
            emptyTitle="No services found"
            emptyDescription="Register your first service to get started."
          />
        )}
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="animate-fade-in w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200/60 dark:border-slate-800/80">
            <div className="border-b border-slate-200 dark:border-slate-800 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Register Service</h2>
            </div>
            <form onSubmit={handleCreateSubmit} className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Name</label>
                  <input
                    name="name"
                    required
                    className="input-field mt-1 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Slug</label>
                  <input
                    name="slug"
                    required
                    pattern="^[a-z0-9][a-z0-9-]*[a-z0-9]$"
                    className="input-field mt-1 w-full"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Type</label>
                    <select
                      name="service_type"
                      defaultValue="api"
                      className="select-field mt-1 w-full"
                    >
                      <option value="api">API</option>
                      <option value="frontend">Frontend</option>
                      <option value="worker">Worker</option>
                      <option value="library">Library</option>
                      <option value="infrastructure">Infrastructure</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Group</label>
                    <select
                      name="service_group"
                      defaultValue=""
                      className="select-field mt-1 w-full"
                    >
                      <option value="">— unset —</option>
                      {GROUPS.filter((g) => g.value).map((g) => (
                        <option key={g.value} value={g.value!}>
                          {g.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Description</label>
                  <textarea
                    name="description"
                    rows={3}
                    className="input-field mt-1 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Language</label>
                  <input
                    name="language"
                    className="input-field mt-1 w-full"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="btn-primary disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
              {createMutation.isError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">Failed to create service. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
